import React from 'react';
import { StudentData } from '../utils/types';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { filterStudents } from '../utils/dataUtils';
import { Sidebar as ProSidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';

interface ShortlistProps {
  shortlist: StudentData[];
  removeFromShortlist: (student: StudentData) => void;
  darkMode: boolean;
}

const Shortlist: React.FC<ShortlistProps> = ({ shortlist, removeFromShortlist, darkMode }) => {
  // Helper to generate CSV string
  const generateCSV = (students: StudentData[]) => {
    const header = ['University', 'Name', 'Email', 'Internal ID', 'Phone'];
    const rows = students.map(s => [
      s.university,
      s.name,
      s.email,
      s.Internal_ID,
      s.phone
    ]);
    return [header, ...rows].map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')).join('\n');
  };

  // Download handler
  const handleDownload = async () => {
    if (shortlist.length === 0) return;
    const zip = new JSZip();
    // Add CSV
    const csv = generateCSV(shortlist);
    zip.file('shortlist.csv', csv);
    // Add resumes
    await Promise.all(shortlist.map(async (student: StudentData) => {
      if (student.Internal_ID) {
        try {
          const response = await fetch(`/byid/${student.Internal_ID}.pdf`);
          if (response.ok) {
            const blob = await response.blob();
            zip.file(`${student.Internal_ID}.pdf`, blob);
          }
        } catch (e) {
          // Ignore missing files
        }
      }
    }));
    // Generate and save zip
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, 'shortlist.zip');
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Shortlisted Students</h2>
      <button
        onClick={handleDownload}
        disabled={shortlist.length === 0}
        className="mb-4 px-4 py-2 bg-[#00A0DF] text-white rounded hover:bg-[#00A0DF]/90 disabled:opacity-50"
      >
        Download Shortlist
      </button>
      {shortlist.length === 0 ? (
        <p className="text-gray-500">No students in your shortlist yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300">
            <thead>
              <tr className={`${darkMode ? 'bg-[#1d2142] text-white' : 'bg-gray-100'}`}>
                <th className="px-4 py-2 border">University</th>
                <th className="px-4 py-2 border">Name</th>
                <th className="px-4 py-2 border">Email</th>
                <th className="px-4 py-2 border">Internal ID</th>
                <th className="px-4 py-2 border">Phone</th>
                <th className="px-4 py-2 border">Remove</th>
              </tr>
            </thead>
            <tbody>
              {shortlist.map(student => (
                <tr key={student.Internal_ID}>
                  <td className="px-4 py-2 border">{student.university}</td>
                  <td className="px-4 py-2 border">{student.name}</td>
                  <td className="px-4 py-2 border">{student.email}</td>
                  <td className="px-4 py-2 border">{student.Internal_ID}</td>
                  <td className="px-4 py-2 border">{student.phone}</td>
                  <td className="px-4 py-2 border text-center">
                    <button
                      onClick={() => removeFromShortlist(student)}
                      className="text-red-500 hover:text-red-700 font-bold"
                      title="Remove from shortlist"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Shortlist; 